import React from 'react';
import { Sparkles, Trash2, Sun, Moon } from 'lucide-react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import ChatInterface, { Message } from '../ChatInterface';
import { VoiceAgent } from '../VoiceAgent';
import { Theme, createTheme } from '../../types/theme';
import { ActiveView } from '../../types';
import { AppConfig } from '../../types/types';
import { ConnectionDetails } from '../../hooks/useConnectionDetails';

interface FullscreenViewProps {
    theme: Theme;
    onThemeChange: (theme: Theme) => void;
    hasSession: boolean;
    clearSession: () => void;
    onViewChange: (view: ActiveView) => void;
    agentName?: string;
    messages: Message[];
    addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
    updateMessage: (id: string, updates: Partial<Message>) => void;
    userName: string | null;
    sessionId: string | null;
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    showVoiceInSplit: boolean;
    connectionDetails: ConnectionDetails | null;
    appConfig: AppConfig;
    isVoiceSessionActive: boolean;
    handleDisconnect: () => void;
    companyName?: string;
}

const FullscreenView: React.FC<FullscreenViewProps> = ({
    theme,
    onThemeChange,
    hasSession,
    clearSession,
    onViewChange,
    agentName,
    messages,
    addMessage,
    updateMessage,
    userName,
    sessionId,
    setMessages,
    showVoiceInSplit,
    connectionDetails,
    appConfig,
    isVoiceSessionActive,
    handleDisconnect,
    companyName
}) => {
    const handleThemeToggle = () => {
        const newMode = theme.mode === 'dark' ? 'light' : 'dark';
        onThemeChange(createTheme(newMode, theme.primaryColor));
    };

    return (
        <div className="relative flex flex-col h-screen" style={{
            backgroundColor: theme.backgroundColor,
            color: theme.textColor
        }}>
            {/* Fullscreen Header */}
            <div
                className="flex items-center justify-between px-6 py-2.5 border-b transition-colors duration-300 flex-shrink-0"
                style={{
                    borderColor: theme.borderColor,
                    backgroundColor: theme.surfaceColor,
                    height: '48px'
                }}
            >
                <div className="flex items-center space-x-2">
                    <Sparkles
                        size={16}
                        className="animate-pulse"
                        style={{ color: theme.primaryColor }}
                    />
                    <span
                        className="text-sm font-medium flex items-center space-x-2"
                        style={{ color: theme.textColor }}
                    >
                        Smart Agent
                    </span>
                </div>

                <div className="flex items-center space-x-2">
                    {hasSession && (
                        <button
                            onClick={clearSession}
                            className="p-2 rounded-lg transition-all duration-200 hover:opacity-80"
                            style={{
                                color: theme.textSecondaryColor,
                                backgroundColor: `${theme.surfaceColor}80`
                            }}
                            title="Clear session and start new"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}

                    {/* Theme Controls */}
                    <button
                        onClick={handleThemeToggle}
                        className="p-2 rounded-lg transition-all duration-200 hover:opacity-80"
                        style={{
                            color: theme.textSecondaryColor,
                            backgroundColor: `${theme.surfaceColor}80`
                        }}
                    >
                        {theme.mode === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 min-h-0 h-[calc(100vh-48px)]">
                <div
                    className={`transition-all duration-300 border-r flex ${showVoiceInSplit ? 'w-3/4' : 'w-full'
                        }`}
                    style={{
                        borderColor: showVoiceInSplit ? theme.borderColor : 'transparent',
                        backgroundColor: theme.backgroundColor
                    }}
                >
                    <div className={`w-full h-full transition-all duration-300 ${!showVoiceInSplit ? 'max-w-5xl mx-auto' : ''}`}>
                        <ChatInterface
                            viewMode="fullscreen"
                            onViewChange={onViewChange}
                            theme={theme}
                            agentName={agentName}
                            messages={messages}
                            addMessage={addMessage}
                            updateMessage={updateMessage}
                            userName={userName}
                            sessionId={sessionId}
                            setMessages={setMessages}
                            companyName={companyName}
                        />
                    </div>
                </div>

                {showVoiceInSplit && connectionDetails && (
                    <div
                        className="w-1/4 h-[calc(100vh-48px)] transition-all duration-300 overflow-hidden"
                        style={{
                            backgroundColor: theme.backgroundColor,
                            borderLeft: `1px solid ${theme.borderColor}`
                        }}
                    >
                        <LiveKitRoom
                            token={connectionDetails.token}
                            serverUrl={appConfig.liveKitUrl}
                            connect={isVoiceSessionActive}
                            audio={true}
                            video={true}
                            onDisconnected={handleDisconnect}
                            className="h-full pointer-events-none"
                        >
                            <div className="pointer-events-auto h-full">
                                <VoiceAgent
                                    appConfig={appConfig}
                                    disabled={!isVoiceSessionActive}
                                    sessionStarted={isVoiceSessionActive}
                                    isWidget={false}
                                    onDisconnect={handleDisconnect}
                                    theme={theme}
                                />
                            </div>
                            <RoomAudioRenderer />
                        </LiveKitRoom>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FullscreenView;
